// i18n.ts — i18next bootstrap + the active-locale accessor (SM-286).
//
// Two kinds of localized text meet in the app:
//
//   1. CHROME copy (nav labels, buttons, settings rows, Yes/No, …) — bundled
//      message catalogs under src/locales/{lng}/{namespace}.json, rendered via
//      react-i18next's t(). English ships eagerly (it is the fallback and the
//      default, so the first paint never flashes raw keys); every other
//      language's catalogs are code-split and lazy-loaded on first switch via
//      import.meta.glob — a language with no bundled catalogs (e.g. one added
//      at runtime through the MCP languages admin) simply falls back to
//      English chrome while catalog CONTENT still localizes via the API.
//
//   2. CATALOG content (market/event/category/outcome names, rules, …) —
//      localized SERVER-side: the fetchers in queries.ts append
//      `?locale=<active>` (see localeQS) and the backend overlays translation
//      rows. The client never translates catalog content itself.
//
// The active locale is module state (not React state) so non-component code —
// query-key factories, formatters, outcome label helpers — can read it
// synchronously via getActiveLocale(). Persistence: localStorage "sm_lang"
// (device-scoped, works anonymously); signed-in users additionally sync it to
// their profile via LanguageProvider (PATCH /api/me/profile {locale}).
import i18next from "i18next";
import { initReactI18next } from "react-i18next";

const STORAGE_KEY = "sm_lang";

export const DEFAULT_LOCALE = "en";

// {lng, namespace} out of a src/locales/<lng>/<ns>.json module path.
const LOCALE_PATH_RE = /locales\/([^/]+)\/([^/]+)\.json$/;

// English catalogs — eager so the fallback language is always resident and
// t() has real strings on the very first render (no async gap, no key flash).
const EN_MODULES = import.meta.glob("../locales/en/*.json", { eager: true });

// Every language's catalogs — lazy. Each JSON becomes its own chunk; a
// language's files are imported only when that language is first activated.
const LOCALE_MODULES = import.meta.glob("../locales/*/*.json");

function bundlesFrom(modules: Record<string, unknown>): Record<string, Record<string, object>> {
  const out: Record<string, Record<string, object>> = {};
  for (const [path, mod] of Object.entries(modules)) {
    const m = path.match(LOCALE_PATH_RE);
    if (!m) continue;
    const [, lng, ns] = m;
    (out[lng] ??= {})[ns] = (mod as { default: object }).default;
  }
  return out;
}

function readStoredLocale(): string | null {
  try {
    return typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
  } catch {
    return null;
  }
}

// hasStoredLocale — true when THIS device carries an explicit choice. The
// LanguageProvider boot order (localStorage → profile.locale → en) uses it to
// decide whether a signed-in profile's locale may take over.
export function hasStoredLocale(): boolean {
  return readStoredLocale() !== null;
}

// Module-level active locale — set before i18next init so the first render
// (and the first round of query keys / fetchers) already speaks the persisted
// language.
let activeLocale = readStoredLocale() || DEFAULT_LOCALE;

export function getActiveLocale(): string {
  return activeLocale;
}

// Languages whose bundled catalogs are already registered with i18next.
const loadedLocales = new Set<string>([DEFAULT_LOCALE]);

// loadLocaleResources — import + register every bundled catalog for `lng`.
// Resolves immediately for already-loaded languages; a language without
// bundled files resolves too (chrome falls back to en — deliberate, see
// header comment).
export async function loadLocaleResources(lng: string): Promise<void> {
  if (loadedLocales.has(lng)) return;
  const entries = Object.entries(LOCALE_MODULES).filter(([path]) =>
    path.includes(`/locales/${lng}/`),
  );
  const loaded = await Promise.all(
    entries.map(async ([path, load]) => [path, (await load()) as { default: object }] as const),
  );
  for (const [path, mod] of loaded) {
    const m = path.match(LOCALE_PATH_RE);
    if (!m) continue;
    i18next.addResourceBundle(lng, m[2], mod.default, true, true);
  }
  loadedLocales.add(lng);
}

// setActiveLocale — the single write path for the active language:
// catalogs loaded → module accessor + localStorage + <html lang> → i18next
// (whose languageChanged event re-renders every useTranslation consumer).
// Query invalidation and profile sync are the LanguageProvider's job — this
// module stays dependency-free so lib code can import it without cycles.
export async function setActiveLocale(code: string): Promise<void> {
  await loadLocaleResources(code).catch(() => {
    /* missing/broken catalog chunk → keep en chrome, still switch content */
  });
  activeLocale = code;
  try {
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, code);
  } catch {
    /* localStorage unavailable (private mode) — in-memory only */
  }
  if (typeof document !== "undefined") document.documentElement.lang = code;
  await i18next.changeLanguage(code);
}

void i18next.use(initReactI18next).init({
  lng: activeLocale,
  fallbackLng: DEFAULT_LOCALE,
  // Empty string in a catalog means "not translated" — fall through to en
  // instead of rendering a blank label.
  returnEmptyString: false,
  defaultNS: "common",
  resources: bundlesFrom(EN_MODULES),
  // React already escapes interpolated values.
  interpolation: { escapeValue: false },
  // Re-render consumers when a lazily-loaded bundle lands, not only on
  // languageChanged — covers the boot path where the persisted language's
  // catalogs arrive after the first paint.
  react: { bindI18nStore: "added removed" },
});

// index.html ships lang="en"; reflect the persisted choice at boot (a11y /
// screen readers) — setActiveLocale keeps it current afterwards.
if (typeof document !== "undefined") document.documentElement.lang = activeLocale;
// Warm the persisted language's catalogs (fire-and-forget; bindI18nStore
// re-renders when they land).
if (activeLocale !== DEFAULT_LOCALE) void loadLocaleResources(activeLocale);

export default i18next;
